// "Change Payment Method" for an order already placed and still awaiting the
// Seller/Provider decision. Lists the buyer's saved Stripe cards (the wallet's
// own list), adds a card through the existing wallet flow, and sends only the
// chosen PaymentMethod id; the server verifies it and keeps the same order.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Link } from 'react-router-dom';
import { useStripe } from '@stripe/react-stripe-js';
import { isAuthenticated } from '../../auth';
import { listePM_createStripeCusTomer } from '../../auth/stripe';
import { changeOrderPaymentMethodAPI, syncStripeOrderAPI, getAvailablePaymentMethodsAPI, attachWalletMethodAPI } from '../../auth/paymentsApi';
import WalletPayButton from './WalletPayButton';
import { orderText as t } from './orderManagementText';
import { errorKey, expiryText, isCardExpired, shortCard } from './orderPaymentMethodState';

const ChangePaymentMethodModal = ({ show, onHide, orderId, line, currentCard, addCardUrl, preferNewest = false, onUpdated }) => {
  const stripe = useStripe();
  const [cards, setCards] = useState({ status: 'idle', list: [] });
  const [selected, setSelected] = useState('');
  const [wallets, setWallets] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const currentId = line?.paymentIntent?.payment_method || currentCard?.id || '';

  const loadCards = useCallback(() => {
    const token = isAuthenticated()?.token;
    setCards({ status: 'loading', list: [] });
    return listePM_createStripeCusTomer(token).then(data => {
      if (data?.error) throw new Error(data.error);
      const list = data?.paymentMethods || [];
      setCards({ status: 'ready', list });
      const usable = list.filter(card => !isCardExpired(card));
      // Back from "Add Card": Stripe lists the newest card first.
      const preferred = preferNewest ? usable.find(card => card.id !== currentId) : null;
      setSelected((preferred || usable.find(card => card.id === currentId) || {}).id || '');
    }).catch(() => setCards({ status: 'error', list: [] }));
  }, [currentId, preferNewest]);

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    setError(''); setBusy(false);
    loadCards();
    getAvailablePaymentMethodsAPI().then(data => { if (active) setWallets(data?.checkout || {}); }).catch(() => {});
    return () => { active = false; };
  }, [show, loadCards]);

  const submit = async paymentMethodId => {
    if (inFlight.current || !paymentMethodId) return false;
    inFlight.current = true; setBusy(true); setError('');
    try {
      const result = await changeOrderPaymentMethodAPI(orderId, line._id, paymentMethodId);
      if (result.status === 'requires_action' && result.clientSecret) {
        // The bank step is asked for the NEW card only.
        if (!stripe) throw Object.assign(new Error(t('pmBankAuthFailed')), { code: 'BANK_STEP' });
        const confirmation = await stripe.confirmCardPayment(result.clientSecret);
        await syncStripeOrderAPI(orderId).catch(() => {});
        if (confirmation.error) {
          await onUpdated?.({ ...result, bankStepFailed: true }, { keepOpen: true });
          throw Object.assign(new Error(t('pmBankAuthFailed')), { code: 'BANK_STEP' });
        }
      }
      await onUpdated?.(result);
      return true;
    } catch (e) {
      setError(e.code === 'BANK_STEP' ? e.message : t(errorKey(e)));
      // A decline or a refused state changes the order: show its real status.
      if (['PAYMENT_METHOD_DECLINED', 'PAYMENT_ALREADY_AUTHORIZED', 'PAYMENT_METHOD_LOCKED', 'ORDER_CHANGED'].includes(e.code)) await onUpdated?.(null, { keepOpen: true });
      return false;
    } finally { inFlight.current = false; setBusy(false); }
  };

  const chosen = cards.list.find(card => card.id === selected);
  const unchanged = !selected || selected === currentId;
  return <Modal show={show} onHide={busy ? undefined : onHide} centered dialogClassName="opm-modal" aria-labelledby="opm-modal-title">
    <Modal.Header closeButton={!busy}>
      <Modal.Title id="opm-modal-title" as="h2" className="opm-modal__title">{t('pmChange')}</Modal.Title>
    </Modal.Header>
    <Modal.Body aria-busy={busy}>
      <p className="opm-modal__intro">{t('pmModalIntro')}</p>
      {currentCard?.last4 && <p className="opm-modal__current"><span>{t('pmCurrent')}</span><strong>{shortCard(currentCard)}</strong></p>}
      <fieldset className="opm-cards" disabled={busy}>
        <legend>{t('pmSavedCards')}</legend>
        {cards.status === 'loading' && <p role="status" className="opm-muted">{t('pmLoadingCards')}</p>}
        {cards.status === 'error' && <div role="alert" className="opm-notice opm-notice--error">{t('pmCardsError')} <button type="button" className="opm-link" onClick={loadCards}>{t('pmRetry')}</button></div>}
        {cards.status === 'ready' && !cards.list.length && <p className="opm-muted">{t('pmNoCards')}</p>}
        {cards.list.map(card => {
          const expired = isCardExpired(card), current = card.id === currentId;
          return <label key={card.id} className={`opm-card-option${selected === card.id ? ' is-selected' : ''}${expired ? ' is-disabled' : ''}`}>
            <input type="radio" name="opm-card" value={card.id} checked={selected === card.id} disabled={expired} onChange={() => { setSelected(card.id); setError(''); }} />
            <span className="opm-card-option__label">{shortCard(card)}</span>
            {expiryText(card) && <span className="opm-card-option__exp">{expiryText(card)}</span>}
            {current && <span className="opm-tag">{t('pmCurrentBadge')}</span>}
            {expired && <span className="opm-tag opm-tag--muted">{t('pmExpiredBadge')}</span>}
          </label>;
        })}
      </fieldset>
      <Link className="opm-add" to={addCardUrl} aria-disabled={busy} onClick={event => { if (busy) event.preventDefault(); }}>+ {t('addCard')}</Link>
      {['applePay', 'googlePay'].filter(wallet => wallets[wallet]).map(wallet => <WalletPayButton key={wallet} wallet={wallet} amountCents={line?.payment?.amountCents}
        onPaymentMethod={async id => { try { const pm = await attachWalletMethodAPI({ paymentMethodId: id, method: wallet === 'applePay' ? 'apple_pay' : 'google_pay' }); return submit(pm.paymentMethodId); } catch (e) { setError(t(errorKey(e))); return false; } }} />)}
      {chosen && !unchanged && <p className="opm-modal__current opm-modal__current--new"><span>{t('pmNew')}</span><strong>{shortCard(chosen)}</strong></p>}
      {error && <div role="alert" className="opm-notice opm-notice--error">{error}</div>}
    </Modal.Body>
    <Modal.Footer>
      <button type="button" className="ov-btn ov-btn-outline opm-footer-btn opm-footer-btn--secondary" disabled={busy} onClick={onHide}>{t('pmCancel')}</button>
      <button type="button" className="ov-btn ov-btn-primary opm-footer-btn opm-footer-btn--primary opm-no-pulse" disabled={busy || unchanged} onClick={() => submit(selected)}>{busy ? t('pmUpdating') : t('pmUpdate')}</button>
    </Modal.Footer>
  </Modal>;
};
export default ChangePaymentMethodModal;
